import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from 'dompurify';
import { FaPlus, FaTrash, FaArrowLeft, FaFileAlt, FaLink, FaSave, FaPaperclip } from "react-icons/fa";
import styles from "./SifatSiyosati.module.css";
import { testUrl } from "../home/BaseData";

const SifatSiyosatiSDEng = ({ hideFiles = true }) => {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const mode = searchParams.get("mode") || "view";
    const shouldDisable = mode !== 'edit' && hideFiles;

    // State for Grouped Data
    const [groupedData, setGroupedData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Batch operations
    const [modifiedGroupIds, setModifiedGroupIds] = useState(new Set());
    const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());

    // Incoming New Entry (Bottom Row)
    const [newEntry, setNewEntry] = useState({
        sd_code: '',
        description_en: '',
        link: '',
        file: null
    });

    const hasNewData = Boolean(newEntry.sd_code || newEntry.description_en || newEntry.link || newEntry.file);

    const fetchSDData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${testUrl}/api/quality-policy-sd-en/`);
            const rawData = response.data;
            processData(rawData);
        } catch (error) {
            console.error("Error fetching SD data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Normalize merged section codes: "SD-E1.910-01" → "SD-E1.9.10-01"
    const normalizeSDCode = (code) =>
        (code || '').replace(/\.(\d{3,})(?=[-.]|$)/g, (_, seg) => {
            const section = parseInt(seg.slice(0, -2), 10);
            const sub = parseInt(seg.slice(-2), 10);
            return `.${section}.${sub}`;
        });

    const naturalSortKey = (str) =>
        normalizeSDCode(str || '').replace(/(\d+)/g, (n) => n.padStart(10, '0'));

    const processData = (rawData) => {
        const groups = {};
        rawData.forEach(item => {
            const code = item.sd_code || "UNCATEGORIZED_" + item.id;

            if (!groups[code]) {
                groups[code] = {
                    masterId: item.id,
                    sd_code: item.sd_code,
                    description_en: item.description_en,
                    link: item.link,
                    relatedIds: [],
                    files: [],
                    newFiles: [],
                    deletedIds: []
                };
            }

            if (item.file) {
                const isMaster = (item.id === groups[code].masterId);
                groups[code].files.push({
                    id: item.id,
                    url: item.file,
                    name: `File (${item.id})`,
                    isMaster: isMaster
                });
            }

            if (item.id !== groups[code].masterId) {
                groups[code].relatedIds.push(item.id);
            }
        });

        const sorted = Object.values(groups).sort((a, b) => {
            const ka = naturalSortKey(a.sd_code);
            const kb = naturalSortKey(b.sd_code);
            return ka < kb ? -1 : ka > kb ? 1 : 0;
        });

        setGroupedData(sorted);
        setModifiedGroupIds(new Set());
        setSelectedGroupIds(new Set());
    };

    useEffect(() => {
        fetchSDData();
    }, [i18n.language]);

    const handleBack = () => {
        navigate("/documents/sifat-siyosati");
    };

    // --- Actions ---

    // Update Text Fields (Code, Desc, Link)
    const handleFieldChange = (masterId, field, value) => {
        setGroupedData(prev => prev.map(group => {
            if (group.masterId === masterId) {
                let key = field;
                if (field === 'description') key = 'description_en';
                return { ...group, [key]: value };
            }
            return group;
        }));
        setModifiedGroupIds(prev => new Set(prev).add(masterId));
    };

    // Add New File to Group
    const handleAddFile = (masterId, file) => {
        if (!file) return;
        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
            alert("File must be in pdf format");
            return;
        }

        setGroupedData(prev => prev.map(group => {
            if (group.masterId === masterId) {
                return { ...group, newFiles: [...group.newFiles, file] };
            }
            return group;
        }));
        setModifiedGroupIds(prev => new Set(prev).add(masterId));
    };

    // Delete Existing File 
    const handleDeleteExistingFile = (masterId, fileEntry) => {
        if (!window.confirm("Are you sure you want to delete the file?")) return;
        setGroupedData(prev => prev.map(group => {
            if (group.masterId === masterId) {
                const newFilesList = group.files.filter(f => f.id !== fileEntry.id);
                let newDeletedIds = [...group.deletedIds];

                if (fileEntry.isMaster) {
                    // Master file cleared logic
                } else {
                    newDeletedIds.push(fileEntry.id);
                }

                const masterFileCleared = fileEntry.isMaster ? true : group.masterFileCleared;
                return { ...group, files: newFilesList, deletedIds: newDeletedIds, masterFileCleared };
            }
            return group;
        }));
        setModifiedGroupIds(prev => new Set(prev).add(masterId));
    };

    const handleDeleteNewFile = (masterId, index) => {
        if (!window.confirm("Are you sure you want to delete the file?")) return;
        setGroupedData(prev => prev.map(group => {
            if (group.masterId === masterId) {
                const updatedNew = group.newFiles.filter((_, i) => i !== index);
                return { ...group, newFiles: updatedNew };
            }
            return group;
        }));
    };

    // --- New Entry Logic ---
    const handleNewEntryChange = (field, value) => {
        if (field === 'file' && value) {
            if (value.type !== 'application/pdf' && !value.name.toLowerCase().endsWith('.pdf')) {
                alert("File must be in pdf format");
                return;
            }
        }
        setNewEntry(prev => ({ ...prev, [field]: value }));
    };

    const handleClearNewEntryFile = () => {
        setNewEntry(prev => ({ ...prev, file: null }));
    };

    const handleAddNewToTable = () => {
        if (!newEntry.sd_code) {
            alert("SD Code is mandatory!");
            return;
        }

        const tempId = `temp_${Date.now()}`;
        const newGroup = {
            masterId: tempId,
            sd_code: newEntry.sd_code,
            description_en: newEntry.description_en,
            link: newEntry.link,
            file: newEntry.file,
            files: [],
            newFiles: newEntry.file ? [newEntry.file] : [],
            relatedIds: [],
            deletedIds: [],
            isNewGroup: true
        };

        setGroupedData(prev => [...prev, newGroup]);
        setModifiedGroupIds(prev => new Set(prev).add(tempId));
        setNewEntry({ sd_code: '', description_en: '', link: '', file: null });
    };

    // --- Save Logic ---
    const handleSaveChanges = async () => {
        if (modifiedGroupIds.size === 0 && !hasNewData) return;

        try {
            const promises = [];

            // 1. Existing Groups
            Array.from(modifiedGroupIds).forEach(masterId => {
                const group = groupedData.find(g => g.masterId === masterId);
                if (!group) return;

                const isTemp = String(group.masterId).startsWith('temp_');

                if (isTemp) {
                    // Create New Group from List
                    if (group.newFiles.length === 0) {
                        const form = new FormData();
                        form.append('sd_code', group.sd_code);
                        form.append('description_en', group.description_en);
                        if (group.link) form.append('link', group.link);

                        if (group.file) form.append('file', group.file);

                        promises.push(axios.post(`${testUrl}/api/quality-policy-sd-en/`, form));
                    } else {
                        group.newFiles.forEach(file => {
                            const form = new FormData();
                            form.append('sd_code', group.sd_code);
                            form.append('description_en', group.description_en);
                            if (group.link) form.append('link', group.link);
                            form.append('file', file);
                            promises.push(axios.post(`${testUrl}/api/quality-policy-sd-en/`, form));
                        });
                    }

                } else {
                    const masterData = {
                        sd_code: group.sd_code || '',
                        description_en: group.description_en || '',
                        link: group.link || ''
                    };

                    if (group.masterFileCleared) {
                        masterData.file = null;
                    }

                    promises.push(axios.patch(`${testUrl}/api/quality-policy-sd-en/${group.masterId}/`, masterData));

                    group.relatedIds.forEach(relId => {
                        const relData = {
                            sd_code: group.sd_code || '',
                            description_en: group.description_en || '',
                            link: group.link || ''
                        };
                        promises.push(axios.patch(`${testUrl}/api/quality-policy-sd-en/${relId}/`, relData));
                    });

                    group.deletedIds.forEach(delId => {
                        promises.push(axios.delete(`${testUrl}/api/quality-policy-sd-en/${delId}/`));
                    });

                    group.newFiles.forEach(file => {
                        const newForm = new FormData();
                        newForm.append('sd_code', group.sd_code || '');
                        newForm.append('description_en', group.description_en || '');
                        if (group.link) newForm.append('link', group.link);
                        newForm.append('file', file);
                        promises.push(axios.post(`${testUrl}/api/quality-policy-sd-en/`, newForm));
                    });
                }
            });

            // 2. Process NEW Entry (Inline) if not added to list
            if (hasNewData) {
                if (!newEntry.sd_code) {
                    alert("SD Code is mandatory for new row!");
                    return;
                }
                const newForm = new FormData();
                newForm.append('sd_code', newEntry.sd_code);
                newForm.append('description_en', newEntry.description_en);
                if (newEntry.link) newForm.append('link', newEntry.link);
                if (newEntry.file) {
                    newForm.append('file', newEntry.file);
                }
                promises.push(axios.post(`${testUrl}/api/quality-policy-sd-en/`, newForm));
            }

            await Promise.all(promises);
            alert("Saved!");
            setNewEntry({ sd_code: '', description_en: '', link: '', file: null });
            fetchSDData();

        } catch (err) {
            console.error(err);
            if (err.response && err.response.data) {
                alert("Error: " + JSON.stringify(err.response.data));
            } else {
                alert("Save error!");
            }
        }
    };

    // Bulk Delete
    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedGroupIds(new Set(groupedData.map(g => g.masterId)));
        else setSelectedGroupIds(new Set());
    };
    const handleSelectRow = (id) => {
        const s = new Set(selectedGroupIds);
        if (s.has(id)) s.delete(id);
        else s.add(id);
        setSelectedGroupIds(s);
    };
    const handleBulkDelete = async () => {
        if (selectedGroupIds.size === 0) return;
        if (!window.confirm("Delete selected?")) return;
        try {
            let ids = [];
            selectedGroupIds.forEach(mid => {
                const g = groupedData.find(x => x.masterId === mid);
                if (g) {
                    if (!String(mid).startsWith('temp_')) ids.push(mid);
                    ids = ids.concat(g.relatedIds || []);
                }
            });
            if (ids.length > 0) {
                await Promise.all(ids.map(id => axios.delete(`${testUrl}/api/quality-policy-sd-en/${id}/`)));
            }
            fetchSDData();
        } catch (e) { alert("Error"); }
    };

    if (loading) return <div className={styles.loading}>Loading...</div>;

    return (
        <div className={styles.mainContainer} style={{ background: '#f8f9fa' }}>
            <div className={styles.container} style={{ maxWidth: '1450px', padding: '0 40px', paddingBottom: '100px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #dee2e6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <button onClick={handleBack} className={styles.closeBtn} style={{ background: '#e9ecef', borderRadius: '4px', width: '36px', height: '36px' }}><FaArrowLeft /></button>
                        <h1 style={{ margin: 0, fontSize: '24px', color: '#343a40', fontWeight: '700' }}>Documents List (English)</h1>
                    </div>
                </div>

                <div className={styles.sdTableWrapper}>
                    <table className={styles.sdTableContainer}>
                        <thead>
                            <tr>
                                {mode === 'edit' && <th className={styles.colCheckbox} style={{ width: '50px' }}><input type="checkbox" onChange={handleSelectAll} checked={groupedData.length > 0 && selectedGroupIds.size === groupedData.length} /></th>}
                                <th style={{ width: '150px' }}>SD CODE</th>
                                <th style={{ minWidth: '400px' }}>DESCRIPTION</th>
                                <th style={{ width: '250px' }}>LINK</th>
                                <th style={{ width: '300px' }}>FILE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupedData.map(group => (
                                <tr key={group.masterId} style={{ background: modifiedGroupIds.has(group.masterId) ? '#fff3cd' : (!group.link && group.files.length === 0 && group.newFiles.length === 0 ? '#ffe6e6' : undefined) }}>
                                    {mode === 'edit' && (
                                        <td className={styles.colCheckbox} style={{ textAlign: 'center' }}>
                                            <input type="checkbox" checked={selectedGroupIds.has(group.masterId)} onChange={() => handleSelectRow(group.masterId)} />
                                        </td>
                                    )}
                                    <td style={{ verticalAlign: 'middle' }}>
                                        {mode === 'edit' ? (
                                            <input className={styles.adminInput} value={group.sd_code} onChange={(e) => handleFieldChange(group.masterId, 'sd_code', e.target.value)} />
                                        ) : <span className={styles.sdCodeBadge}>{group.sd_code}</span>}
                                    </td>
                                    <td style={{ verticalAlign: 'middle' }}>
                                        {mode === 'edit' ? (
                                            <textarea className={styles.adminTextarea} value={group.description_en} onChange={(e) => handleFieldChange(group.masterId, 'description', e.target.value)} />
                                        ) : <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(group[`description_${i18n.language}`] || group.description_en, { ADD_ATTR: ['style', 'target'] }) }} />}
                                    </td>
                                    <td style={{ verticalAlign: 'middle' }}>
                                        {mode === 'edit' ? (
                                            <input className={styles.adminInput} value={group.link || ''} onChange={(e) => handleFieldChange(group.masterId, 'link', e.target.value)} />
                                        ) : (group.link ? (
                                            shouldDisable
                                                ? <span className={styles.sdLink} style={{ color: '#6c757d', cursor: 'not-allowed', opacity: 0.6 }}><FaLink /> Link</span>
                                                : <a href={group.link} target="_blank" rel="noreferrer" className={styles.sdLink}><FaLink /> Link</a>
                                        ) : <span>-</span>)}
                                    </td>
                                    <td style={{ verticalAlign: 'middle' }}>
                                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', overflowX: 'auto', maxWidth: '400px', paddingBottom: '4px', scrollbarWidth: 'thin' }}>
                                            {group.files.map((f) => (
                                                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#eafcf3', padding: '4px 8px', borderRadius: '4px', border: '1px solid #c3e6cb', flexShrink: 0 }}>
                                                    {shouldDisable ? (
                                                        <span className={styles.sdLink} style={{ color: '#6c757d', cursor: 'not-allowed', opacity: 0.6, fontSize: '12px' }}><FaFileAlt /> File</span>
                                                    ) : (
                                                        <a href={f.url} target="_blank" rel="noreferrer" className={styles.sdLink} style={{ color: '#155724', fontSize: '12px' }}><FaFileAlt /> File</a>
                                                    )}
                                                    {mode === 'edit' && <button className={`${styles.fileBtn} ${styles.fileBtnDel}`} style={{ width: '20px', height: '20px' }} onClick={() => handleDeleteExistingFile(group.masterId, f)}><FaTrash size={9} /></button>}
                                                </div>
                                            ))}
                                            {group.newFiles.map((f, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fff3cd', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ffeeba', flexShrink: 0 }}>
                                                    <span style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}><FaFileAlt /> File</span>
                                                    {mode === 'edit' && <button className={`${styles.fileBtn} ${styles.fileBtnDel}`} style={{ width: '20px', height: '20px' }} onClick={() => handleDeleteNewFile(group.masterId, i)}><FaTrash size={9} /></button>}
                                                </div>
                                            ))}
                                            {mode === 'edit' && (
                                                <div style={{ flexShrink: 0 }}>
                                                    <input type="file" id={`add-${group.masterId}`} style={{ display: 'none' }} onChange={(e) => handleAddFile(group.masterId, e.target.files[0])} />
                                                    <label htmlFor={`add-${group.masterId}`} className={`${styles.fileBtn} ${styles.fileBtnAdd}`} style={{ width: '24px', height: '24px' }}><FaPlus size={10} /></label>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {/* NEW ENTRY EMPTY ROW */}
                            {mode === 'edit' && (
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td className={styles.colCheckbox} style={{ textAlign: 'center' }}>
                                        <input type="checkbox" disabled />
                                    </td>
                                    <td style={{ verticalAlign: 'middle' }}>
                                        <input className={styles.adminInput} placeholder="New SD Code" value={newEntry.sd_code} onChange={(e) => handleNewEntryChange('sd_code', e.target.value)} />
                                    </td>
                                    <td style={{ verticalAlign: 'middle' }}>
                                        <textarea className={styles.adminTextarea} placeholder="Description" value={newEntry.description_en} onChange={(e) => handleNewEntryChange('description_en', e.target.value)} style={{ minHeight: '40px' }} />
                                    </td>
                                    <td style={{ verticalAlign: 'middle' }}>
                                        <input className={styles.adminInput} placeholder="https://" value={newEntry.link} onChange={(e) => handleNewEntryChange('link', e.target.value)} />
                                    </td>
                                    <td style={{ verticalAlign: 'middle' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                                            {/* File Chooser Area */}
                                            {newEntry.file ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fff3cd', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ffeeba' }}>
                                                    <span style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                                        <FaFileAlt /> File
                                                    </span>
                                                    <button
                                                        className={`${styles.fileBtn} ${styles.fileBtnDel}`}
                                                        style={{ width: '20px', height: '20px' }}
                                                        onClick={handleClearNewEntryFile}
                                                    >
                                                        <FaTrash size={9} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <input type="file" id="new-entry-file" style={{ display: 'none' }} onChange={(e) => handleNewEntryChange('file', e.target.files[0])} />
                                                    <label
                                                        htmlFor="new-entry-file"
                                                        className={`${styles.fileBtn}`}
                                                        style={{ width: '24px', height: '24px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '1px solid #ccc', background: '#e9ecef', borderRadius: '4px' }}
                                                        title="Select File"
                                                    >
                                                        <FaPaperclip size={12} color="#495057" />
                                                    </label>
                                                </>
                                            )}

                                            <button
                                                onClick={handleAddNewToTable}
                                                className={styles.sdAddIconButton}
                                                style={{ width: '24px', height: '24px', borderRadius: '4px', background: '#0d6efd', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' }}
                                                title="Add Row"
                                            >
                                                <FaPlus size={12} color="white" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {mode === 'edit' && (
                <div className={styles.bottomActionBar}>
                    {selectedGroupIds.size > 0 && (
                        <button onClick={handleBulkDelete} style={{ background: '#dc3545', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <FaTrash size={12} /> Delete Selected ({selectedGroupIds.size})
                        </button>
                    )}
                    {(modifiedGroupIds.size > 0 || hasNewData) && (
                        <button onClick={handleSaveChanges} style={{ background: '#0d6efd', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <FaSave size={12} /> Save Changes ({modifiedGroupIds.size + (hasNewData ? 1 : 0)})
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
export default SifatSiyosatiSDEng;
